import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  TextInput,
  Linking,
  Platform,
  StyleSheet,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { formatExternalUrl } from '../../utils/formatExternalUrl';
import { isSignedInAccount } from '../../utils/liveTechSpecs';
import { canManageEventPost } from '../../utils/eventPostAccess';
import {
  isVideoMime,
  subscribeEventMessages,
  sendEventMessage,
  getEventChatIdentity,
  setEventChatIdentity,
  setEventTalkEnabled,
} from '../../utils/eventPostsService';
import { CANVA_MENU_GREEN } from '../../utils/hamburgerMenu';

// BUG-105: the Canva green (#2F9A84, sampled from the Canva menu), not the brighter #03BB8D.
const GREEN = CANVA_MENU_GREEN;
const BLUE = '#166FFF';
const LIGHT = '#E2DEDE';
const MUTED = '#787A99';
const PAGE_BG = '#231F20';

/** "2026-10-04" → "Oct 04 2026" (kept as typed if it isn't a date). */
function formatEventDate(value) {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!match) return String(value);
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  return `${month} ${String(date.getDate()).padStart(2, '0')} ${date.getFullYear()}`;
}

function openLink(url) {
  const href = formatExternalUrl(url);
  if (href) Linking.openURL(href).catch(() => {});
}

function EventPoster({ poster }) {
  if (!poster?.url) {
    return <View style={[styles.poster, { backgroundColor: 'rgba(255,255,255,0.08)' }]} />;
  }
  if (isVideoMime(poster.mimeType, poster.name)) {
    if (Platform.OS === 'web') {
      return (
        <video
          src={poster.url}
          controls
          playsInline
          style={{ width: '100%', aspectRatio: '400 / 565', objectFit: 'cover', display: 'block', backgroundColor: '#000' }}
        />
      );
    }
    return (
      <TouchableOpacity style={[styles.poster, styles.videoFallback]} onPress={() => openLink(poster.url)}>
        <Text style={styles.videoFallbackText}>▶ Play video</Text>
      </TouchableOpacity>
    );
  }
  return <Image source={{ uri: poster.url }} style={styles.poster} resizeMode="cover" />;
}

// BUG-105: duck_chat.png faces right, toward the message text. The first sender keeps it
// unflipped and later senders alternate.
export function isDuckFlipped(index) {
  return index % 2 === 1;
}

/**
 * Event post card. Minimized shows the poster + title block; maximized adds
 * organizer, event details, talk (chat) and "posted by".
 */
export function EventPostCard({ event, currentUser, onEdit, onDelete }) {
  const navigation = useNavigation();
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [chatAlias, setChatAlias] = useState('');
  const [aliasDraft, setAliasDraft] = useState('');
  const [loadingAlias, setLoadingAlias] = useState(false);
  const [savingAlias, setSavingAlias] = useState(false);
  const [chatError, setChatError] = useState('');
  const [talkEnabled, setTalkEnabled] = useState(event.talkEnabled !== false);
  const [savingTalkSetting, setSavingTalkSetting] = useState(false);
  const [sending, setSending] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const signedIn = isSignedInAccount(currentUser);

  // Only listen to the chat while the card is open.
  useEffect(() => {
    if (!expanded || !event?.id) return undefined;
    return subscribeEventMessages(event.id, setMessages, (err) => console.warn('Event messages', err));
  }, [expanded, event?.id]);

  useEffect(() => {
    setTalkEnabled(event.talkEnabled !== false);
  }, [event.id, event.talkEnabled]);

  useEffect(() => {
    let active = true;
    setChatAlias('');
    setAliasDraft('');
    setChatError('');
    if (!expanded || !event?.id || !currentUser?.uid) {
      setLoadingAlias(false);
      return () => { active = false; };
    }
    setLoadingAlias(true);
    getEventChatIdentity(event.id, currentUser.uid)
      .then((alias) => {
        if (active) {
          setChatAlias(alias);
          setAliasDraft(alias);
          setLoadingAlias(false);
        }
      })
      .catch((err) => {
        console.error('Load event chat identity failed', err);
        if (active) {
          setChatError('Could not load your chat identity. Please try again.');
          setLoadingAlias(false);
        }
      });
    return () => { active = false; };
  }, [expanded, event?.id, currentUser?.uid]);

  const handleSaveAlias = async () => {
    const alias = aliasDraft.trim();
    if (alias.length < 2 || alias.length > 24) {
      setChatError('Your anonymous ID must be between 2 and 24 characters.');
      return;
    }
    setSavingAlias(true);
    setChatError('');
    try {
      const savedAlias = await setEventChatIdentity(event.id, currentUser.uid, alias);
      setChatAlias(savedAlias);
      setAliasDraft(savedAlias);
    } catch (err) {
      console.error('Save event chat identity failed', err);
      setChatError('Could not save your anonymous ID. Please try again.');
    } finally {
      setSavingAlias(false);
    }
  };

  const handleTalkToggle = async () => {
    const nextValue = !talkEnabled;
    setSavingTalkSetting(true);
    setChatError('');
    try {
      await setEventTalkEnabled(event.id, nextValue);
      setTalkEnabled(nextValue);
    } catch (err) {
      console.error('Update event talk setting failed', err);
      setChatError('Could not update talk settings. Please try again.');
    } finally {
      setSavingTalkSetting(false);
    }
  };

  const handleSend = async () => {
    if (!signedIn) {
      navigation.navigate('Login');
      return;
    }
    const text = draft.trim();
    if (!text || sending || !chatAlias || !talkEnabled) return;
    setSending(true);
    setChatError('');
    try {
      await sendEventMessage(event.id, {
        userId: currentUser.uid,
        alias: chatAlias,
        text,
      });
      setDraft('');
    } catch (err) {
      console.error('Send event message failed', err);
      setChatError('Could not send your message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const websites = (Array.isArray(event.websites) ? event.websites : []).filter((w) => w?.url);
  const ticket = event.ticketLink?.url ? event.ticketLink : null;
  const pointer = Platform.OS === 'web' ? { cursor: 'pointer' } : null;
  const canManage = canManageEventPost(event, currentUser);
  const messageGroups = messages.reduce((groups, message) => {
    const senderKey = message.userId || message.id;
    const previousGroup = groups[groups.length - 1];
    if (previousGroup?.senderKey === senderKey) {
      previousGroup.messages.push(message);
    } else {
      groups.push({
        senderKey,
        alias: message.alias || 'Anonymous',
        messages: [message],
      });
    }
    return groups;
  }, []);

  return (
    <View style={[styles.card, optionsOpen && styles.cardWithOpenOptions]}>
      <EventPoster poster={event.poster} />

      <View style={[styles.panel, optionsOpen && styles.panelWithOpenOptions]}>
        {/* Title, websites | date, tickets, venue */}
        <View style={[styles.topRow, optionsOpen && styles.topRowWithOpenOptions]}>
          <View style={styles.topLeft}>
            <Text style={styles.title}>{event.title}</Text>
            <View>
              {websites.map((site, index) => (
                <Text key={`${site.url}-${index}`} style={[styles.website, pointer]} onPress={() => openLink(site.url)} numberOfLines={1}>
                  {site.text || site.url}
                </Text>
              ))}
            </View>
          </View>
          <View style={styles.topRight}>
            <View style={styles.dateOptionsRow}>
              <Text style={styles.date}>{formatEventDate(event.date)}</Text>
              {canManage ? (
                <View style={styles.optionsActions}>
                  <TouchableOpacity
                    style={[styles.optionsButton, pointer]}
                    onPress={(e) => {
                      e?.stopPropagation?.();
                      setOptionsOpen((open) => !open);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="Event options"
                    accessibilityState={{ expanded: optionsOpen }}
                  >
                    <Ionicons name="ellipsis-vertical" size={22} color={LIGHT} />
                  </TouchableOpacity>
                  {optionsOpen ? (
                    <View style={styles.optionsMenu}>
                      <TouchableOpacity
                        style={styles.optionsMenuItem}
                        onPress={(e) => {
                          e?.stopPropagation?.();
                          setOptionsOpen(false);
                          onEdit?.(event);
                        }}
                        accessibilityRole="button"
                      >
                        <Text style={styles.optionsMenuText}>Edit</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.optionsMenuItem}
                        onPress={(e) => {
                          e?.stopPropagation?.();
                          setOptionsOpen(false);
                          onDelete?.(event);
                        }}
                        accessibilityRole="button"
                      >
                        <Text style={[styles.optionsMenuText, styles.optionsDeleteText]}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  ) : null}
                </View>
              ) : null}
            </View>
            {ticket ? (
              <Text style={[styles.ticket, pointer]} onPress={() => openLink(ticket.url)}>
                {ticket.text || 'Tickets'}
              </Text>
            ) : null}
            {event.venueId ? (
              <Text
                style={[styles.venueLink, pointer]}
                onPress={() => navigation.navigate('Venue', { venueId: event.venueId })}
              >
                {event.venueName || 'VENUE LINK'}
              </Text>
            ) : null}
          </View>
        </View>

        {expanded ? (
          <>
            {/* Organizer */}
            <View style={styles.organizerSection}>
              <Text style={styles.organizedBy}>organized by</Text>
              <View style={styles.organizerRow}>
                {event.organizer?.photo?.url
                  ? <Image source={{ uri: event.organizer.photo.url }} style={styles.organizerPhoto} />
                  : <View style={styles.organizerPhoto} />}
                <View style={styles.organizerText}>
                  <Text style={styles.organizerName}>{event.organizer?.name}</Text>
                  <Text style={styles.organizerDescription}>{event.organizer?.description}</Text>
                </View>
              </View>
            </View>

            {/* Event details */}
            <View style={styles.detailsSection}>
              <Text style={styles.detailsHeader}>EVENT DETAILS</Text>
              <Text style={styles.detailsText}>{event.details}</Text>
            </View>

            {(talkEnabled || canManage) ? (
              <View style={styles.talkSection}>
                <View style={styles.talkHeaderRow}>
                  <Text style={styles.talkHeader}>talk</Text>
                  {canManage ? (
                    <TouchableOpacity
                      onPress={handleTalkToggle}
                      disabled={savingTalkSetting}
                      style={styles.talkOwnerToggle}
                      accessibilityRole="switch"
                      accessibilityState={{ checked: talkEnabled, disabled: savingTalkSetting }}
                    >
                      <Text style={styles.talkOwnerToggleText}>
                        {savingTalkSetting ? 'Saving…' : talkEnabled ? 'Turn talk off' : 'Turn talk on'}
                      </Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
                {talkEnabled ? (
                  <>
                    {!signedIn ? (
                      <TouchableOpacity onPress={() => navigation.navigate('Login')}>
                        <Text style={styles.chatHint}>Sign in to join the anonymous chat.</Text>
                      </TouchableOpacity>
                    ) : loadingAlias ? (
                      <Text style={styles.chatHint}>Loading your anonymous ID…</Text>
                    ) : !chatAlias ? (
                      <View style={styles.identityForm}>
                        <Text style={styles.chatHint}>Choose your anonymous ID for this event. You can only set it once.</Text>
                        <View style={styles.identityInputRow}>
                          <TextInput
                            value={aliasDraft}
                            onChangeText={setAliasDraft}
                            placeholder="Anonymous ID"
                            placeholderTextColor="rgba(255,255,255,0.55)"
                            maxLength={24}
                            autoCapitalize="none"
                            editable={!savingAlias}
                            style={[styles.input, styles.identityInput]}
                          />
                          <TouchableOpacity
                            onPress={handleSaveAlias}
                            disabled={savingAlias}
                            style={styles.identityButton}
                          >
                            <Text style={styles.identityButtonText}>{savingAlias ? 'Saving…' : 'Set ID'}</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ) : (
                      <>
                        <Text style={styles.chatHint}>Chatting as {chatAlias}</Text>
                        <View style={styles.messages}>
                          {messageGroups.map((group, index) => (
                            <View key={`${group.senderKey}-${group.messages[0]?.id}`} style={styles.messageGroup}>
                              <Image
                                source={require('../../../assets/icons/duck_chat.png')}
                                style={[styles.duckAvatar, isDuckFlipped(index) && styles.duckFlipped]}
                                resizeMode="contain"
                              />
                              <View style={styles.messageBody}>
                                <Text style={styles.messageUser}>{group.alias}</Text>
                                {group.messages.map((message) => (
                                  <Text key={message.id} style={styles.messageText}>{message.text}</Text>
                                ))}
                              </View>
                            </View>
                          ))}
                        </View>
                        <View style={styles.inputWrap}>
                          <TextInput
                            value={draft}
                            onChangeText={setDraft}
                            onSubmitEditing={handleSend}
                            onFocus={() => { if (!signedIn) navigation.navigate('Login'); }}
                            placeholder="Enter Message here"
                            placeholderTextColor="rgba(255,255,255,0.85)"
                            editable={!sending}
                            returnKeyType="send"
                            style={styles.input}
                          />
                        </View>
                      </>
                    )}
                  </>
                ) : (
                  <Text style={styles.chatHint}>Talk is turned off for this event.</Text>
                )}
                {chatError ? <Text style={styles.chatError}>{chatError}</Text> : null}
              </View>
            ) : null}

            <Text style={styles.postedBy}>POSTED BY {event.username || 'User'}</Text>
          </>
        ) : null}

        {/* Expand (small framed box) / collapse (dash) */}
        <TouchableOpacity
          onPress={() => setExpanded((prev) => !prev)}
          style={[styles.toggle, pointer]}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          accessibilityRole="button"
          accessibilityLabel={expanded ? 'Minimize event' : 'Maximize event'}
        >
          {expanded ? <View style={styles.toggleDash} /> : <View style={styles.toggleBox} />}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // Fills its grid cell like Gear Swap's listingCard.
  card: {
    width: '100%',
    alignSelf: 'stretch',
    position: 'relative',
    gap: 9,
  },
  cardWithOpenOptions: {
    zIndex: 10,
    elevation: 10,
  },
  poster: {
    width: '100%',
    aspectRatio: 400 / 565,
  },
  videoFallback: {
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoFallbackText: {
    color: '#fff',
    fontSize: 16,
  },
  panel: {
    position: 'relative',
    overflow: 'visible',
    borderRadius: 24,
    borderWidth: 4,
    borderColor: GREEN,
    paddingHorizontal: 22,
    paddingTop: 21,
    paddingBottom: 36,
    gap: 5,
  },
  panelWithOpenOptions: {
    zIndex: 10,
    elevation: 10,
  },
  optionsActions: {
    position: 'relative',
    zIndex: 20,
    alignItems: 'center',
  },
  optionsButton: {
    width: 30,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionsMenu: {
    position: 'absolute',
    top: 28,
    right: 0,
    minWidth: 120,
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 5,
    zIndex: 20,
  },
  optionsMenuItem: {
    minHeight: 36,
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  optionsMenuText: {
    color: '#333',
    fontSize: 14,
    fontFamily: 'Inter',
  },
  optionsDeleteText: {
    color: '#e74c3c',
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  topLeft: {
    flex: 1,
    gap: 7,
  },
  topRight: {
    alignItems: 'flex-end',
    gap: 3,
    maxWidth: '50%',
  },
  // BUG-105: RN-web gives every View z-index 0, so the open options menu sat under the
  // organizer section below and clicks on Delete landed there. Lift the whole row while open.
  topRowWithOpenOptions: {
    zIndex: 30,
    elevation: 30,
  },
  dateOptionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
    zIndex: 10,
  },
  title: {
    color: BLUE,
    fontSize: 18,
    fontFamily: 'Inter',
  },
  website: {
    color: LIGHT,
    fontSize: 12,
    fontFamily: 'Inter',
  },
  date: {
    color: LIGHT,
    fontSize: 18,
    fontFamily: 'Inter',
    fontWeight: '700',
    textAlign: 'right',
  },
  ticket: {
    color: GREEN,
    fontSize: 14,
    fontFamily: 'Inter',
    textAlign: 'right',
  },
  venueLink: {
    color: LIGHT,
    fontSize: 20,
    fontFamily: 'Computer Says No',
    textAlign: 'right',
  },
  organizerSection: {
    marginTop: 12,
    gap: 17,
  },
  organizedBy: {
    color: GREEN,
    fontSize: 20,
    fontFamily: 'Inter',
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  organizerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 13,
  },
  organizerPhoto: {
    width: 77,
    height: 77,
    backgroundColor: '#fff',
  },
  organizerText: {
    flex: 1,
    minHeight: 77,
    gap: 3,
  },
  organizerName: {
    color: LIGHT,
    fontSize: 15,
    fontFamily: 'Inter',
  },
  organizerDescription: {
    color: LIGHT,
    fontSize: 13,
    fontFamily: 'Inter',
  },
  detailsSection: {
    marginTop: 12,
    alignItems: 'flex-end',
    gap: 7,
  },
  detailsHeader: {
    alignSelf: 'stretch',
    color: MUTED,
    fontSize: 20,
    fontFamily: 'Inter',
    fontWeight: '700',
    textDecorationLine: 'underline',
    textAlign: 'right',
  },
  detailsText: {
    alignSelf: 'stretch',
    color: LIGHT,
    fontSize: 20,
    fontFamily: 'Computer Says No',
    textAlign: 'right',
  },
  talkSection: {
    marginTop: 12,
    paddingBottom: 20,
    gap: 5,
  },
  talkHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  talkHeader: {
    color: LIGHT,
    fontSize: 20,
    fontFamily: 'Inter',
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  talkOwnerToggle: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: GREEN,
  },
  talkOwnerToggleText: {
    color: GREEN,
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: '700',
  },
  chatHint: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    fontFamily: 'Inter',
  },
  chatError: {
    color: '#ff7b7b',
    fontSize: 13,
    fontFamily: 'Inter',
  },
  identityForm: {
    gap: 8,
  },
  identityInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  identityInput: {
    flex: 1,
    minWidth: 0,
  },
  identityButton: {
    paddingVertical: 9,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: GREEN,
  },
  identityButtonText: {
    color: PAGE_BG,
    fontSize: 13,
    fontFamily: 'Inter',
    fontWeight: '700',
  },
  messages: {
    gap: 10,
  },
  messageGroup: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  duckAvatar: {
    width: 38,
    height: 34,
  },
  duckFlipped: {
    transform: [{ scaleX: -1 }],
  },
  messageBody: {
    flex: 1,
    gap: 3,
    alignItems: 'flex-start',
  },
  messageUser: {
    color: '#fff',
    fontSize: 13,
    fontFamily: 'Gaegu',
    fontWeight: '700',
  },
  messageText: {
    color: GREEN,
    fontSize: 14,
    fontFamily: 'Roboto',
  },
  inputWrap: {
    marginTop: 19,
    minHeight: 39,
    paddingHorizontal: 19,
    paddingVertical: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
    borderRadius: 20,
    justifyContent: 'center',
  },
  input: {
    color: '#fff',
    fontSize: 24,
    fontFamily: 'Computer Says No',
    textAlign: 'center',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  postedBy: {
    color: LIGHT,
    fontSize: 24,
    fontFamily: 'Computer Says No',
  },
  toggle: {
    position: 'absolute',
    right: 24,
    bottom: 14,
    width: 20,
    height: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleBox: {
    width: 20,
    height: 11,
    backgroundColor: PAGE_BG,
    borderRadius: 5,
    borderWidth: 4,
    borderColor: '#fff',
  },
  toggleDash: {
    width: 20,
    height: 5,
    backgroundColor: '#fff',
  },
});

export default EventPostCard;
